import React, { useEffect, useState } from 'react';

interface SaveDraftModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  onDiscard?: () => void;
  title?: string;
  subtitle?: string;
  isLoading?: boolean;
  confirmButtonText?: string;
  discardButtonText?: string;
  iconPath?: string;
}

export const SaveDraftModal: React.FC<SaveDraftModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  onDiscard,
  title = 'Save Post?',
  subtitle = 'You can save this and publish later',
  isLoading = false,
  confirmButtonText = 'Save as Draft',
  discardButtonText = 'Discard',
  iconPath = '/savepost-icon.png'
}) => {
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    if (isOpen) {
      // Trigger animation after mount
      setTimeout(() => setIsAnimating(true), 10);
    } else {
      setIsAnimating(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleDiscard = () => {
    onClose();
    onDiscard?.();
  };

  return (
    <div 
      className={`fixed inset-0 bg-black bg-opacity-30 z-50 flex justify-center items-center p-4 transition-opacity duration-300 ${
        isAnimating ? 'opacity-100' : 'opacity-0'
      }`}
      onClick={onClose}
    >
      <div 
        className={`bg-white rounded-2xl shadow-lg w-full max-w-md overflow-hidden transition-all duration-300 ease-out ${
          isAnimating ? 'opacity-100 scale-100 translate-y-0' : 'opacity-0 scale-95 translate-y-4'
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header with illustration */}
        <div className="bg-gray-50 pt-5 flex items-center justify-center">
          <img 
            src={iconPath} 
            alt="Save" 
            className="w-[139px] h-auto object-contain"
          />
        </div>
        
        {/* Modal Content */}
        <div className="p-6">
          <h2 className="text-2xl font-bold text-gray-800 mb-2">{title}</h2>
          <p className="text-sm text-gray-500 mb-6">{subtitle}</p>
          
          {/* Action Buttons */}
          <div className="flex gap-4">
            <button
              onClick={handleDiscard}
              disabled={isLoading}
              className="flex-1 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg px-4 py-2 hover:bg-gray-50 disabled:opacity-50 transition-colors"
            >
              {discardButtonText}
            </button>
            <button
              onClick={onConfirm}
              disabled={isLoading}
              className="flex-1 text-sm font-medium text-white bg-forestGreen-100 rounded-lg px-4 py-2 hover:bg-green-700 disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
            >
              {isLoading ? (
                <>
                  <div className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>Saving...</span>
                </>
              ) : (
                confirmButtonText
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SaveDraftModal;

